/**
 * Pointer-based swipe / drag detection (build brief B3, E2).
 * One helper for mouse drag on desktop and finger swipe on touch, via Pointer Events.
 *
 *   const off = onSwipe(el, {
 *     threshold: 40,                       // px before a move counts as a swipe
 *     onMove (dx, dy, e) {},               // live drag offset (only after the pointer has moved a little)
 *     onSwipe (dir, { dx, dy, dt }) {},    // 'left' | 'right' | 'up' | 'down'
 *     onEnd ({ dx, dy, moved, dir }) {}    // always fires on release / cancel
 *   })
 *   off() // teardown
 *
 * Set `touch-action` on the element yourself: 'pan-y' lets vertical scrolling stay native while
 * horizontal swipes reach this helper; 'none' hands every direction to it.
 */
export function onSwipe (el, opts = {}) {
  const threshold = opts.threshold || 40
  let id = null
  let sx = 0
  let sy = 0
  let dx = 0
  let dy = 0
  let t0 = 0
  let moved = false

  const reset = () => { id = null; dx = 0; dy = 0; moved = false }

  const down = e => {
    if (e.button !== undefined && e.button !== 0) return
    id = e.pointerId
    sx = e.clientX
    sy = e.clientY
    t0 = Date.now()
    dx = 0
    dy = 0
    moved = false
  }
  const move = e => {
    if (id === null || e.pointerId !== id) return
    dx = e.clientX - sx
    dy = e.clientY - sy
    if (!moved && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) moved = true
    if (moved && opts.onMove) opts.onMove(dx, dy, e)
  }
  const up = e => {
    if (id === null || e.pointerId !== id) return
    const dt = Date.now() - t0
    const ax = Math.abs(dx)
    const ay = Math.abs(dy)
    const quick = dt < 250 && (ax > 24 || ay > 24) // a short flick counts too
    let dir = null
    if (ax > ay && (ax > threshold || quick)) dir = dx < 0 ? 'left' : 'right'
    else if (ay > ax && (ay > threshold || quick)) dir = dy < 0 ? 'up' : 'down'
    if (dir && opts.onSwipe) opts.onSwipe(dir, { dx, dy, dt })
    if (opts.onEnd) opts.onEnd({ dx, dy, moved, dir })
    reset()
  }
  const cancel = e => {
    if (id === null || (e && e.pointerId !== undefined && e.pointerId !== id)) return
    if (opts.onEnd) opts.onEnd({ dx, dy, moved, dir: null, cancelled: true })
    reset()
  }

  el.addEventListener('pointerdown', down)
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', cancel)

  return () => {
    el.removeEventListener('pointerdown', down)
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', cancel)
  }
}
